import type { ComponentType } from 'react';

import BlueCheckmark from '~/assets/icons/BlueCheckmark';
import FacebookLogo from '~/assets/icons/FacebookRound';
import InstagramLogo from '~/assets/icons/Instagram';
import SoleIcon from '~/assets/icons/Sole';
import TikTokLogo from '~/assets/icons/TikTok';
import TswLogo from '~/assets/icons/TswLogo';
import YoutubeLogo from '~/assets/icons/Youtube';
import {
  SOCIAL_HANDLES,
  SOCIAL_MEDIA_URLS
} from '~/components/Social/SocialMediaCard/SocialMediaLinks';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';

type LogoProps = {
  fill: string;
  height: string;
  width: string;
};

type SocialProfile = {
  logo: ComponentType<LogoProps>;
  platform: string;
  url: string;
};

type Account = {
  avatar: ComponentType;
  handle: string;
  name: string;
  profiles: SocialProfile[];
};

function TssAvatar() {
  return (
    <span className="flex size-6 items-center justify-center rounded-full bg-grey-dark">
      <SoleIcon width="15" height="12" />
    </span>
  );
}

function TswAvatar() {
  return (
    <span className="flex size-6 items-center justify-center rounded-full bg-white">
      <TswLogo width="15" height="12.1875" />
    </span>
  );
}

const TSS_ACCOUNT: Account = {
  avatar: TssAvatar,
  handle: SOCIAL_HANDLES.tss,
  name: 'Sole Supplier',
  profiles: [
    {
      logo: InstagramLogo,
      platform: 'Instagram',
      url: SOCIAL_MEDIA_URLS.tss.instagram
    },
    {
      logo: TikTokLogo,
      platform: 'TikTok',
      url: SOCIAL_MEDIA_URLS.tss.tiktok
    },
    {
      logo: FacebookLogo,
      platform: 'Facebook',
      url: SOCIAL_MEDIA_URLS.tss.facebook
    },
    {
      logo: YoutubeLogo,
      platform: 'Youtube',
      url: SOCIAL_MEDIA_URLS.tss.youtube
    }
  ]
};

const TSW_ACCOUNT: Account = {
  avatar: TswAvatar,
  handle: SOCIAL_HANDLES.tsw,
  name: 'Sole Womens',
  profiles: [
    {
      logo: InstagramLogo,
      platform: 'Instagram',
      url: SOCIAL_MEDIA_URLS.tsw.instagram
    },
    {
      logo: TikTokLogo,
      platform: 'TikTok',
      url: SOCIAL_MEDIA_URLS.tsw.tiktok
    },
    {
      logo: FacebookLogo,
      platform: 'Facebook',
      url: SOCIAL_MEDIA_URLS.tsw.facebook
    }
  ]
};

function SocialAccount({ avatar: Avatar, handle, name, profiles }: Account) {
  const fireSocialLink = useNavigationLinkInteraction('footer_social');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Avatar />
        <div className="flex items-center gap-1 text-grey-hide">
          {handle}
          <span aria-label="Verified" role="img">
            <BlueCheckmark />
          </span>
        </div>
      </div>

      <ul className="flex items-center gap-6 desktop:gap-8">
        {profiles.map(({ logo: Logo, platform, url }, index) => (
          <li key={platform}>
            <a
              aria-label={`${name} on ${platform} [New Window]`}
              className="block"
              href={url}
              onClick={() => fireSocialLink(url, index + 1)}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Logo fill="white" height="24" width="24" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function FollowUs() {
  return (
    <div className="flex flex-col gap-4">
      <SocialAccount {...TSS_ACCOUNT} />
      <hr className="border-grey-read" />
      <SocialAccount {...TSW_ACCOUNT} />
    </div>
  );
}
